import React, { useEffect, useState } from 'react';
import { Alert, Confirm, DropDownFlex, Input, SimpleButton, Spinner } from "@tag/tag-components-react-v4";
import CreateCustomerService from "../services/CreateCustomerService";
import PipelineStatusDisplay from "../../General/components/PipelineStatusDisplay";
import { ProjectAccents } from "../../General/config/projectAccents";
import styles from '../CreateCustomer/CreateCustomer.module.scss';

interface CreateCustomerFormProps {
    organisationId?: string;
    corporateSite?: string;
    showCreateAnother?: boolean;
    onPipelineComplete?: (result: string | null) => void;
}

const CreateCustomerForm: React.FC<CreateCustomerFormProps> = ({ organisationId, corporateSite, showCreateAnother = true, onPipelineComplete }) => {
    const [appGroupNames, setAppGroupNames] = useState<string[]>([]);
    const [selectedAppGroup, setSelectedAppGroup] = useState<string>('');
    const [tenantName, setTenantName] = useState<string>('');
    const [tenantNameError, setTenantNameError] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);
    const [loadingAppGroups, setLoadingAppGroups] = useState<boolean>(true);
    const [confirmVisible, setConfirmVisible] = useState<boolean>(false);
    const [alert, setAlert] = useState<{ type: any, message: string }>();
    const [buildUrl, setBuildUrl] = useState<string>();
    const [pipelineId, setPipelineId] = useState<number>();
    const [buildId, setBuildId] = useState<number>();

    useEffect(() => {
        CreateCustomerService.getAppGroupNames()
            .then((names) => {
                setAppGroupNames(names);
                setLoadingAppGroups(false);
            })
            .catch(() => {
                setAlert({ type: 'error', message: 'Failed to load app group names' });
                setLoadingAppGroups(false);
            });
    }, []);

    const validateTenantName = (value: string): string => {
        if (!value) return '';
        if (value !== value.toLowerCase()) {
            return 'Tenant name must be all lower case';
        }
        if (!/^[a-z0-9]+$/.test(value)) {
            return 'Tenant name must not contain special characters, dashes, or dots';
        }
        return '';
    };

    const handleTenantNameChange = (value: string) => {
        setTenantName(value);
        setTenantNameError(validateTenantName(value));
    };

    const autoFixTenantName = () => {
        const fixed = tenantName.toLowerCase().replace(/[^a-z0-9]/g, '');
        setTenantName(fixed);
        setTenantNameError(validateTenantName(fixed));
    };

    const handleCreateClick = () => {
        const error = validateTenantName(tenantName);
        if (error) {
            setTenantNameError(error);
            return;
        }
        setConfirmVisible(true);
    };

    const handleConfirmCreate = async () => {
        setConfirmVisible(false);
        setLoading(true);

        try {
            const result = await CreateCustomerService.createCustomer(selectedAppGroup, tenantName, organisationId);

            if (result.success) {
                setBuildUrl(result.buildUrl);
                setPipelineId(result.pipelineId);
                setBuildId(result.buildId);
                const message = organisationId
                    ? 'Pipeline triggered successfully! The customer will be automatically linked to the workspace.'
                    : 'Pipeline triggered successfully!';
                setAlert({ type: 'success', message });
            } else {
                setAlert({ type: 'error', message: result.errorMessage || 'Failed to trigger pipeline' });
            }
        } catch (e: any) {
            setAlert({ type: 'error', message: e.message || 'An error occurred, please contact HSC DevOps' });
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setBuildUrl(undefined);
        setPipelineId(undefined);
        setBuildId(undefined);
        setTenantName('');
        setSelectedAppGroup('');
        setAlert(undefined);
    };

    const isFormValid = selectedAppGroup && tenantName && !tenantNameError;

    if (loading) {
        return <Spinner full>Triggering pipeline...</Spinner>;
    }

    if (buildUrl) {
        return (
            <div className={styles.SuccessContainer}>
                <div className={styles.SuccessCard}>
                    <h3 className={styles.SuccessHeading}>Pipeline triggered successfully!</h3>
                    {organisationId && (
                        <p>
                            The customer will be automatically linked to workspace organisation <strong>{organisationId}</strong> once creation completes.
                        </p>
                    )}
                    {pipelineId && buildId ? (
                        <PipelineStatusDisplay
                            apiBaseUrl={process.env.REACT_APP_SELF_SERVICE_CARECOMPLIANCE_API_URL}
                            pipelineId={pipelineId}
                            runId={buildId}
                            buildUrl={buildUrl}
                            onComplete={onPipelineComplete}
                        />
                    ) : (
                        <p>
                            <a href={buildUrl} target="_blank" rel="noopener noreferrer">
                                View Pipeline Run
                            </a>
                        </p>
                    )}
                    {showCreateAnother && (
                        <div className={styles.SuccessActions}>
                            <SimpleButton
                                accent={ProjectAccents.CareCompliance}
                                onClick={resetForm}
                            >
                                Create Another
                            </SimpleButton>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    return (
        <>
            {alert && (
                <Alert
                    closeTransition="fade"
                    type={alert.type}
                    onClosed={() => setAlert(undefined)}
                >
                    {alert.message}
                </Alert>
            )}
            {loadingAppGroups ? (
                <Spinner />
            ) : (
                <div className={styles.FormCard}>
                    <div className={styles.FormGroup}>
                        <label className={styles.FormLabel}>App Group Name</label>
                        <DropDownFlex
                            data={['Select App Group', ...appGroupNames]}
                            onChange={(e) => {
                                const val = (e.target as HTMLSelectElement).value;
                                setSelectedAppGroup(val === 'Select App Group' ? '' : val);
                            }}
                        />
                    </div>
                    <div className={styles.FormGroup}>
                        <label className={styles.FormLabel}>Tenant Name</label>
                        <span className={styles.FormHint}>Lowercase alphanumeric only, no special characters</span>
                        <Input
                            placeholder="e.g. acmecorp"
                            onChange={(e) => handleTenantNameChange(e.target.value)}
                            value={tenantName}
                        />
                        {tenantNameError && (
                            <div className={styles.ValidationError}>
                                {tenantNameError}
                                <button
                                    type="button"
                                    className={styles.FixButton}
                                    onClick={autoFixTenantName}
                                >
                                    Fix name
                                </button>
                            </div>
                        )}
                    </div>
                    <div className={styles.FormActions}>
                        <SimpleButton
                            accent={ProjectAccents.CareCompliance}
                            disabled={!isFormValid}
                            onClick={handleCreateClick}
                        >
                            {organisationId ? 'Create & Link to Workspace' : 'Create Customer'}
                        </SimpleButton>
                    </div>
                </div>
            )}
            <Confirm
                visible={confirmVisible}
                type="information"
                accent={ProjectAccents.CareCompliance}
                heading="Confirm Customer Creation"
                onPrimaryButtonClick={handleConfirmCreate}
                onCancelButtonClick={() => setConfirmVisible(false)}
                onClose={() => setConfirmVisible(false)}
            >
                Please check the following details carefully before confirming:
                <table className={styles.ConfirmDetails}>
                    <tbody>
                        <tr>
                            <td>App Group Name</td>
                            <td>{selectedAppGroup}</td>
                        </tr>
                        <tr>
                            <td>Tenant Name</td>
                            <td>{tenantName}</td>
                        </tr>
                        {organisationId && (
                            <tr>
                                <td>Organisation ID</td>
                                <td>{organisationId}</td>
                            </tr>
                        )}
                        {corporateSite && (
                            <tr>
                                <td>Corporate Site</td>
                                <td>{corporateSite}</td>
                            </tr>
                        )}
                    </tbody>
                </table>
                {organisationId && (
                    <>
                        <br />
                        <div><b>The customer will be automatically linked to the workspace organisation after creation.</b></div>
                    </>
                )}
            </Confirm>
        </>
    );
};

export default CreateCustomerForm;
